import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

/** One gateway's part in a resync sweep. */
export interface GatewayResyncResult {
  gatewayId: string;
  gatewayName: string | null;
  /**
   * Whether the gateway completed a resync. `false` means its devices' edges
   * went unconfirmed this sweep — not that they are wrong.
   */
  reached: boolean;
  devicesSynced: number;
  edgesReported: number;
  error?: string;
}

/** A link the sweep could not get an assertion for, and why. */
export interface UnconfirmedConnection {
  connectionId: string;
  sourceDeviceId: string;
  sourceDeviceName: string | null;
  targetDeviceName: string | null;
  sourceInterfaceName?: string;
  connectionType: string;
  lastSeenAt: string;
  reason: string;
}

export interface ResyncResult {
  gateways: GatewayResyncResult[];
  devicesSynced: number;
  edgesReported: number;
  /** Links still standing that no gateway spoke for. Listed, never deleted. */
  unconfirmed: UnconfirmedConnection[];
}

export interface ForgetResult {
  forgotten: number;
  skipped: { connectionId: string; reason: string }[];
}

const backendApi = config.backendApi;

/**
 * Ask every gateway in the region to re-state the topology it holds, so the
 * graph can be reconciled against it.
 *
 * This does not re-poll devices: each gateway refreshes its own view on the
 * LLDP collector's schedule, and what a resync repairs is drift between a
 * gateway and the backend.
 */
export function useTopologyResync(organizationId: string | null | undefined, regionId?: string) {
  const queryClient = useQueryClient();

  return useMutation<ResyncResult>({
    mutationFn: async () => {
      const response = await axios.post<ResyncResult>(
        `${backendApi}/api/topology/resync`,
        { regionId: regionId ?? null },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      // A resync prunes edges, so the graph the user is looking at is stale.
      void queryClient.invalidateQueries({ queryKey: ['topology', organizationId] });
    },
  });
}

/**
 * Delete named links that nothing can vouch for.
 *
 * The backend re-derives the unconfirmable set and only deletes the
 * intersection, so a link whose gateway came back in the meantime is left
 * alone rather than removed on the strength of a list that has gone stale.
 */
export function useForgetConnections(organizationId: string | null | undefined, regionId?: string) {
  const queryClient = useQueryClient();

  return useMutation<ForgetResult, Error, string[]>({
    mutationFn: async (connectionIds: string[]) => {
      const response = await axios.post<ForgetResult>(
        `${backendApi}/api/topology/connections/forget`,
        { regionId: regionId ?? null, connectionIds },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['topology', organizationId] });
    },
  });
}
